import type { CandidateDetailsProcedureGroup } from '@tempmee/core';
import { PerformanceBadge } from '@tempmee/react';
import type { InviteCandidate, InviteSkillsView } from '@tempmee/react-blocks';
import type { IListItemData } from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/professionalAdapters';
import { EducationUtils } from '~/utils/EducationUtils';
import { ATTENDANCE_BY_LEVEL, toRating } from '~/utils/candidateBadges';

/** Backend badge type for the attendance coin shown beside a professional's name. */
const ATTENDANCE_BADGE_TYPE = 'attendance-score';

/**
 * The attendance coin for an invite row.
 *
 * Draws the design system's `PerformanceBadge`, the same artwork the Confirm
 * Candidates card uses. The backend also sends a rendered PNG on the badge
 * (`imageUrl`), which is what this row used to show — but that image is the v2
 * artwork, so an office comparing the two screens saw two different coins for
 * the same professional (design, 2026-09-15). The level the badge carries maps
 * onto the DS variant through the table the card already owns.
 *
 * An ungraded professional (level 0) gets no coin, matching `CandidateCard`:
 * a badge stands for something earned (GOTU-7518).
 */
const AttendanceCoin = ({ row }: { row: IListItemData }) => {
  const level = row.badges?.find(({ type }) => type === ATTENDANCE_BADGE_TYPE)?.level;
  const variant = ATTENDANCE_BY_LEVEL[level ?? 0];

  if (!variant) {
    return null;
  }

  return <PerformanceBadge variant={variant} size="small" />;
};

/** Maps one invite row onto the block's presentational candidate shape. */
export const toInviteCandidate = (row: IListItemData): InviteCandidate => ({
  id: row.id,
  name: row.fullName,
  avatarUrl: row.profileImage || undefined,
  rating: toRating(row.rating?.averageRating),
  // The marker claims the professional is free for this shift, so it needs the
  // backend to have actually said so — the "new to you" pool sends nothing and
  // `isAvailable` falls back to `true` there (design, 2026-09-15).
  isAvailable: row.hasKnownAvailability && row.isAvailable,
  attendance: <AttendanceCoin row={row} />,
});

/**
 * Turns a professional's education map into the block's procedure groups.
 *
 * Mirrors the legacy `SkillsView`: DN/DA list their procedures grouped under
 * each specialty, while RDH/FD have no specialty axis and get one flat group.
 * A profession with neither shape returns nothing, and the block renders the
 * header alone.
 */
const toProcedureGroups = (row: IListItemData): CandidateDetailsProcedureGroup[] => {
  const education = row.education ?? {};
  // A professional's education is keyed by profession; prefer the one this shift
  // is for and fall back to whatever they do have, exactly as the legacy view.
  const code = education[row.professionCode] ? row.professionCode : Object.keys(education)[0];

  if (!code) {
    return [];
  }

  const specialtiesAndProcedures = education[code]?.specialtiesAndProcedures ?? {};

  return EducationUtils.toProcedureGroups({
    specialtiesAndProcedures,
    profession: code,
    // Every specialty on file: the invite list is not scoped to one shift's.
    specialties: Object.keys(specialtiesAndProcedures),
    flatLabel: 'Procedures',
  });
};

/**
 * The Skills view for one professional, shown in place of the candidate list
 * when an office opens a row's chevron.
 */
export const toInviteSkills = (row: IListItemData, onBack: () => void): InviteSkillsView => ({
  name: row.fullName,
  avatarUrl: row.profileImage || undefined,
  procedures: toProcedureGroups(row),
  onBack,
});
